import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { XMarkIcon, SpinnerIcon, UploadIcon } from '../../../components/Icons';
import { TARGET_FIELDS, INTERNAL_FIELD_MAP } from './operation/OperationConstants';
import { FileUploadStep, ConfigStep, MappingStep, ReportStep } from './operation/MassUpdateSteps';
import { batchUpsertPropertiesApi } from '../../../services/adminPropertyApiClient';

interface MassUpdateModalProps {
    isOpen: boolean;
    onClose: () => void;
    allowedOwners: string[];
    onComplete: () => void;
}

export const MassUpdateModal: React.FC<MassUpdateModalProps> = ({ isOpen, onClose, allowedOwners, onComplete }) => {
    const [step, setStep] = useState<'file' | 'config' | 'mapping' | 'executing' | 'report'>('file');
    const [workbook, setWorkbook] = useState<XLSX.WorkBook | null>(null);
    const [sheets, setSheets] = useState<string[]>([]);
    const [selectedSheet, setSelectedSheet] = useState('');
    const [selectedOwner, setSelectedOwner] = useState('');
    const [primaryKey, setPrimaryKey] = useState<'codigo' | 'codigo_brp'>('codigo_brp');
    const [operationMode, setOperationMode] = useState<'update' | 'insert'>('update');
    const [headers, setHeaders] = useState<string[]>([]);
    const [pkMapping, setPkMapping] = useState<string>(''); 
    const [mapping, setMapping] = useState<Record<string, string>>({});
    const [isProcessing, setIsProcessing] = useState(false);
    const [results, setResults] = useState({ success: 0, failed: 0, total: 0, errors: [] as string[] });

    useEffect(() => { 
        if (!isOpen) { 
            setStep('file'); 
            setWorkbook(null); 
            setPkMapping(''); 
            setSelectedOwner(''); 
            setMapping({}); 
            setOperationMode('update'); 
        } 
    }, [isOpen]);

    if (!isOpen) return null;

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            const result = evt.target?.result;
            if (!result) return;
            const wb = XLSX.read(result, { type: 'binary' });
            setWorkbook(wb); setSheets(wb.SheetNames); setSelectedSheet(wb.SheetNames[0]); setStep('config');
        };
        reader.readAsBinaryString(file);
    };

    const handleConfigNext = () => {
        if (!workbook || !selectedSheet) return;
        const jsonData = XLSX.utils.sheet_to_json(workbook.Sheets[selectedSheet], { header: 1 }) as any[][];
        const fileHeaders = (jsonData[0] || []).map(h => String(h).trim()).filter(Boolean);
        setHeaders(fileHeaders);
        if (operationMode === 'update') {
            const keyWord = primaryKey === 'codigo' ? 'codigo' : 'brp';
            const foundPk = fileHeaders.find(h => h.toLowerCase().includes(keyWord)) || fileHeaders[0] || '';
            setPkMapping(foundPk);
        }
        const initMap: Record<string, string> = {};
        fileHeaders.forEach(h => {
            if (operationMode === 'update' && h === pkMapping) return;
            const match = TARGET_FIELDS.find(f => f.label.toLowerCase() === h.toLowerCase() || f.key.toLowerCase() === h.toLowerCase());
            if (match) initMap[h] = match.key;
        });
        setMapping(initMap); setStep('mapping');
    };

    const executeUpdate = async () => {
        setIsProcessing(true); setStep('executing');
        const data = XLSX.utils.sheet_to_json(workbook!.Sheets[selectedSheet]) as any[];
        const formattedOwner = selectedOwner.replace(/_/g, '-');
        const batchPayload: Array<{ id?: string; data: Record<string, any> }> = [];

        data.forEach((row) => {
            const rowData: any = {};
            Object.keys(mapping).forEach((h: string) => {
                const dbField = mapping[h];
                let val = row[h]; if (val === undefined || val === null) return;
                if (dbField === 'valor_administracion') val = Number(String(val).replace(/[^0-9]/g, '')) || 0;
                const targetField = INTERNAL_FIELD_MAP[dbField] || dbField;
                if (targetField === 'codigo_brp_o_inmobiliaria') {
                    const sVal = String(val).trim(); const nVal = Number(sVal);
                    val = (!isNaN(nVal) && sVal !== '') ? nVal : sVal;
                }
                rowData[targetField] = val;
            });

            const pkString = String(row[pkMapping] ?? "").trim();
            if (pkString) {
                rowData.owner = formattedOwner;
                if (operationMode === 'insert') {
                    rowData.status = rowData.status || 'Activo';
                    rowData.fecha_agregado_nuevo = new Date().toISOString();
                }
                batchPayload.push({ id: pkString, data: rowData });
            }
        });

        try {
            const processed = await batchUpsertPropertiesApi(batchPayload, true);
            setResults({ success: processed, failed: data.length - processed, total: data.length, errors: [] });
        } catch (err: any) {
            setResults({ success: 0, failed: data.length, total: data.length, errors: [err?.message || 'Error en carga'] });
        } finally {
            setIsProcessing(false); 
            setStep('report');
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-900/90 backdrop-blur-md z-[300] flex items-center justify-center p-4">
            <div className="relative bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden animate-fade-in-up">
                <header className="px-8 py-6 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
                    <div className="flex items-center gap-3"><UploadIcon className="w-6 h-6 text-sky-600" /><h2 className="text-xl font-black uppercase text-gray-800">Carga Masiva de Inventario</h2></div>
                    {step !== 'executing' && <button onClick={onClose} className="text-gray-400 hover:text-red-500"><XMarkIcon className="w-6 h-6"/></button>}
                </header>
                <div className="p-8">
                    {step === 'file' && <FileUploadStep onUpload={handleFileUpload} />}
                    {step === 'config' && <ConfigStep sheets={sheets} selectedSheet={selectedSheet} onSheetChange={setSelectedSheet} selectedOwner={selectedOwner} onOwnerChange={setSelectedOwner} allowedOwners={allowedOwners} primaryKey={primaryKey} onPkTypeChange={setPrimaryKey} operationMode={operationMode} onOperationModeChange={setOperationMode} onNext={handleConfigNext} />}
                    {step === 'mapping' && <MappingStep pkMapping={pkMapping} onPkMappingChange={setPkMapping} headers={headers} primaryKey={primaryKey} selectedOwner={selectedOwner} mapping={mapping} onMappingChange={(h, f) => setMapping({...mapping, [h]: f})} operationMode={operationMode} onBack={() => setStep('config')} onExecute={executeUpdate} />}
                    {step === 'executing' && <div className="py-12 flex flex-col items-center justify-center space-y-6"><div className="relative w-32 h-32 flex items-center justify-center"><SpinnerIcon className="w-32 h-32 text-sky-600 animate-spin absolute" /><span className="text-xl font-black text-sky-900">{Math.round(((results.success + results.failed) / results.total) * 100) || 0}%</span></div><h4 className="text-lg font-black text-gray-800 uppercase">{operationMode === 'update' ? 'Actualizando' : 'Insertando'} {results.total} filas</h4></div>}
                    {step === 'report' && <ReportStep results={results} onComplete={onComplete} operationMode={operationMode} />}
                </div>
            </div>
        </div>
    );
};
